import React, { useState, Fragment } from 'react';
import { FunnelMetrics, FunnelStep, OpportunityRecord } from '../../types/throughput';
import { Card, CardHeader, CardTitle } from '../ui/Card';
import { ArrowRight, ChevronDown, ChevronUp, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
interface ThroughputFunnelProps {
  metrics: FunnelMetrics[];
  opportunities: OpportunityRecord[];
}
const KEY_STEPS: FunnelStep[] = ['Created', 'Converted', 'App Out', 'App Completed', 'Submitted to UW', 'Closing Scheduled', 'Funded'];
const STEP_LABELS: Partial<Record<FunnelStep, string>> = {
  Created: 'Claimed',
  'Closing Scheduled': 'CTC'
};
export function ThroughputFunnel({
  metrics,
  opportunities
}: ThroughputFunnelProps) {
  const [isVisualFunnelOpen, setIsVisualFunnelOpen] = useState(true);
  const [selectedStep, setSelectedStep] = useState<FunnelStep | null>(null);
  const keyMetrics = metrics.filter(m => KEY_STEPS.includes(m.step));
  const maxCount = keyMetrics[0]?.count || 1;
  // Calculate quality breakdown for Submitted to UW
  const submittedOpps = opportunities.filter(o => o.submittedToUWDate);
  const totalSubmitted = submittedOpps.length;
  const dirtyCount = submittedOpps.filter(o => o.isDirtyAtSubmission).length;
  const cleanCount = totalSubmitted - dirtyCount;
  const dirtyPct = totalSubmitted > 0 ? (dirtyCount / totalSubmitted * 100).toFixed(1) : '0.0';
  const cleanPct = totalSubmitted > 0 ? (cleanCount / totalSubmitted * 100).toFixed(1) : '0.0';
  // Calculate at-risk breakdown for App Out
  const appOutOpps = opportunities.filter(o => o.appOutDate);
  const totalAppOut = appOutOpps.length;
  const atRiskCount = appOutOpps.filter(o => o.isAtRiskAtAppOut).length;
  const onTrackCount = totalAppOut - atRiskCount;
  const atRiskPct = totalAppOut > 0 ? (atRiskCount / totalAppOut * 100).toFixed(1) : '0.0';
  const onTrackPct = totalAppOut > 0 ? (onTrackCount / totalAppOut * 100).toFixed(1) : '0.0';
  const INTERACTIVE_STEPS: FunnelStep[] = ['Submitted to UW', 'App Out'];
  const handleStepClick = (step: FunnelStep) => {
    if (INTERACTIVE_STEPS.includes(step)) {
      setSelectedStep(selectedStep === step ? null : step);
    }
  };
  return <Card className="mb-8 p-0 overflow-hidden" noPadding data-id="element-123">
      <CardHeader bordered className="!mb-0" data-id="element-124">
        <div className="flex items-center justify-between w-full" data-id="element-125">
          <CardTitle data-id="element-126">Conversion Funnel</CardTitle>
          <button onClick={() => setIsVisualFunnelOpen(!isVisualFunnelOpen)} className="flex items-center gap-1.5 text-sm font-medium text-[#706E6B] hover:text-[#181818] transition-colors" data-id="element-127">
            {isVisualFunnelOpen ? <>
                Hide visual funnel <ChevronUp className="h-4 w-4" data-id="element-128" />
              </> : <>
                Show visual funnel <ChevronDown className="h-4 w-4" data-id="element-129" />
              </>}
          </button>
        </div>
      </CardHeader>

      <div className="p-8" data-id="element-130">
        {/* Top Section: Numeric Row (Always visible) */}
        <div className="flex items-center justify-between max-w-6xl mx-auto" data-id="element-131">
          {keyMetrics.map((metric, index) => {
          const isLast = index === keyMetrics.length - 1;
          const nextMetric = !isLast ? keyMetrics[index + 1] : null;
          const stepConversion = nextMetric && metric.count > 0 ? (nextMetric.count / metric.count * 100).toFixed(0) : '0';
          const label = STEP_LABELS[metric.step] || metric.step;
          const isInteractive = INTERACTIVE_STEPS.includes(metric.step);
          const isSelected = selectedStep === metric.step;
          return <Fragment key={`num-${metric.step}`} data-id="element-132">
                <div className={clsx('flex flex-col items-center text-center flex-1 rounded-lg p-2 transition-colors', isInteractive && 'cursor-pointer hover:bg-[#F5F5F5]', isSelected && 'bg-[#F5F5F5] ring-1 ring-[#E5E5E5]')} onClick={() => handleStepClick(metric.step)} data-id="element-133">
                  <div className={clsx('text-sm font-medium mb-3', isInteractive ? 'text-[#181818] border-b border-dashed border-[#CCCCCC]' : 'text-[#706E6B]')} data-id="element-134">
                    {label}
                  </div>
                  <div className={clsx('text-3xl font-semibold mb-2', isSelected ? 'text-[#B8960C]' : 'text-[#181818]')} data-id="element-135">
                    {metric.count.toLocaleString()}
                  </div>
                  {index > 0 && <div className="text-xs text-[#706E6B]" data-id="element-136">
                      {metric.overallConversionRate.toFixed(1)}% of total
                    </div>}
                </div>

                {!isLast && <div className="flex flex-col items-center justify-center px-2" data-id="element-137">
                    <ArrowRight className="h-5 w-5 text-[#CCCCCC] mb-1" data-id="element-138" />
                    <span className="text-xs font-medium text-[#706E6B] bg-[#F5F5F5] px-2 py-1 rounded-full" data-id="element-139">
                      {stepConversion}%
                    </span>
                  </div>}
              </Fragment>;
        })}
        </div>

        {/* Bottom Section: Visual Funnel (Collapsible) */}
        <AnimatePresence initial={false} data-id="element-140">
          {isVisualFunnelOpen && <motion.div initial={{
          height: 0,
          opacity: 0,
          marginTop: 0
        }} animate={{
          height: 'auto',
          opacity: 1,
          marginTop: 32
        }} exit={{
          height: 0,
          opacity: 0,
          marginTop: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-141">
              <div className="max-w-3xl mx-auto flex flex-col items-center gap-2 py-2" data-id="element-142">
                {keyMetrics.map((metric, index) => {
              const widthPercentage = Math.max(15, metric.count / maxCount * 100);
              const label = STEP_LABELS[metric.step] || metric.step;
              // Neutral color: from dark slate to light gray
              const lightness = 30 + index * (40 / Math.max(1, keyMetrics.length - 1));
              const barColor = `hsl(215, 12%, ${lightness}%)`;
              const isNarrow = widthPercentage < 35;
              const prevMetric = index > 0 ? keyMetrics[index - 1] : null;
              const stepPct = prevMetric && prevMetric.count > 0 ? (metric.count / prevMetric.count * 100).toFixed(1) : null;
              const isInteractive = INTERACTIVE_STEPS.includes(metric.step);
              const isSelected = selectedStep === metric.step;
              return <Fragment key={`bar-wrapper-${metric.step}`} data-id="element-143">
                      {/* Bar row */}
                      <div className="relative w-full flex items-center justify-center" data-id="element-144">
                        <motion.div initial={{
                    opacity: 0,
                    y: 10,
                    width: '0%'
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    width: `${widthPercentage}%`
                  }} whileHover={{
                    scale: isInteractive ? 1.02 : 1.01
                  }} transition={{
                    duration: 0.5,
                    delay: index * 0.05,
                    ease: [0.4, 0, 0.2, 1],
                    width: {
                      duration: 0.8,
                      delay: index * 0.05,
                      ease: 'easeOut'
                    }
                  }} onClick={() => handleStepClick(metric.step)} className={clsx('relative h-11 rounded-md flex items-center justify-center shadow-sm transition-shadow', isInteractive ? 'cursor-pointer hover:shadow-md ring-offset-2' : 'cursor-default', isSelected && 'ring-2 ring-[#B8960C]')} style={{
                    backgroundColor: barColor
                  }} data-id="element-145">
                          {/* Text inside bar if wide enough */}
                          {!isNarrow && <div className="absolute inset-0 flex items-center justify-between px-4" data-id="element-146">
                              <span className={clsx('text-sm font-medium text-white whitespace-nowrap', isInteractive && 'border-b border-dashed border-white/50')} data-id="element-147">
                                {label}
                              </span>
                              <span className="text-sm font-semibold text-white" data-id="element-148">
                                {metric.count.toLocaleString()}
                              </span>
                            </div>}
                        </motion.div>

                        {/* Text outside bar if too narrow */}
                        {isNarrow && <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    delay: 0.5 + index * 0.05
                  }} className="absolute top-1/2 -translate-y-1/2 flex items-center gap-2 whitespace-nowrap" style={{
                    left: `calc(50% + ${widthPercentage / 2}% + 12px)`
                  }} data-id="element-149">
                            <span className={clsx('text-sm font-medium text-[#706E6B]', isInteractive && 'border-b border-dashed border-[#CCCCCC] cursor-pointer')} onClick={() => handleStepClick(metric.step)} data-id="element-150">
                              {label}
                            </span>
                            <span className="text-sm font-semibold text-[#181818]" data-id="element-151">
                              {metric.count.toLocaleString()}
                            </span>
                          </motion.div>}

                        {/* Conversion rate from previous step — shown to the right */}
                        {stepPct && <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    delay: 0.6 + index * 0.05
                  }} className="absolute right-0 top-1/2 -translate-y-1/2" data-id="element-152">
                            <span className="text-[11px] font-medium text-[#A0A0A0]" data-id="element-153">
                              {stepPct}%
                            </span>
                          </motion.div>}
                      </div>
                    </Fragment>;
            })}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Detail Panel */}
      <AnimatePresence data-id="element-154">
        {selectedStep === 'Submitted to UW' && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="border-t border-[#E5E5E5] bg-[#FAFAFA] overflow-hidden" data-id="element-155">
            <div className="p-6 max-w-3xl mx-auto" data-id="element-156">
              <div className="flex items-center justify-between mb-6" data-id="element-157">
                <div data-id="element-158">
                  <h4 className="text-base font-semibold text-[#181818] mb-1" data-id="element-159">
                    Submission Quality
                  </h4>
                  <p className="text-sm text-[#706E6B]" data-id="element-160">
                    Breakdown of dirty vs clean files at the time of UW
                    submission
                  </p>
                </div>
                <button onClick={() => setSelectedStep(null)} className="p-2 text-[#706E6B] hover:bg-[#E5E5E5] rounded-md transition-colors" data-id="element-161">
                  <X className="h-5 w-5" data-id="element-162" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-8 mb-6" data-id="element-163">
                <div className="bg-white p-4 rounded-lg border border-[#E5E5E5] shadow-sm" data-id="element-164">
                  <div className="flex items-center gap-2 mb-2" data-id="element-165">
                    <div className="w-3 h-3 rounded-full bg-[#04844B]" data-id="element-166" />
                    <span className="text-sm font-medium text-[#706E6B]" data-id="element-167">
                      Clean Files
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-168">
                    <span className="text-2xl font-semibold text-[#181818]" data-id="element-169">
                      {cleanCount}
                    </span>
                    <span className="text-sm font-medium text-[#04844B]" data-id="element-170">
                      {cleanPct}%
                    </span>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#E5E5E5] shadow-sm" data-id="element-171">
                  <div className="flex items-center gap-2 mb-2" data-id="element-172">
                    <div className="w-3 h-3 rounded-full bg-[#EA001E]" data-id="element-173" />
                    <span className="text-sm font-medium text-[#706E6B]" data-id="element-174">
                      Dirty Files
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-175">
                    <span className="text-2xl font-semibold text-[#181818]" data-id="element-176">
                      {dirtyCount}
                    </span>
                    <span className="text-sm font-medium text-[#EA001E]" data-id="element-177">
                      {dirtyPct}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Stacked Bar */}
              {totalSubmitted > 0 && <div data-id="element-178">
                  <div className="flex h-3 rounded-full overflow-hidden bg-[#E5E5E5] mb-2" data-id="element-179">
                    <div className="h-full bg-[#04844B] transition-all duration-1000 ease-out" style={{
                width: `${cleanPct}%`
              }} data-id="element-180" />
                    <div className="h-full bg-[#EA001E] transition-all duration-1000 ease-out" style={{
                width: `${dirtyPct}%`
              }} data-id="element-181" />
                  </div>
                  <div className="flex justify-between text-xs text-[#706E6B] font-medium" data-id="element-182">
                    <span data-id="element-183">{cleanPct}% Clean</span>
                    <span data-id="element-184">{dirtyPct}% Dirty</span>
                  </div>
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* App Out Detail Panel */}
      <AnimatePresence data-id="element-185">
        {selectedStep === 'App Out' && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="border-t border-[#E5E5E5] bg-[#FAFAFA] overflow-hidden" data-id="element-186">
            <div className="p-6 max-w-3xl mx-auto" data-id="element-187">
              <div className="flex items-center justify-between mb-6" data-id="element-188">
                <div data-id="element-189">
                  <h4 className="text-base font-semibold text-[#181818] mb-1" data-id="element-190">
                    App Out Risk Assessment
                  </h4>
                  <p className="text-sm text-[#706E6B]" data-id="element-191">
                    Breakdown of files flagged as &lsquo;At Risk&rsquo; at the
                    App Out stage
                  </p>
                </div>
                <button onClick={() => setSelectedStep(null)} className="p-2 text-[#706E6B] hover:bg-[#E5E5E5] rounded-md transition-colors" data-id="element-192">
                  <X className="h-5 w-5" data-id="element-193" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-8 mb-6" data-id="element-194">
                <div className="bg-white p-4 rounded-lg border border-[#E5E5E5] shadow-sm" data-id="element-195">
                  <div className="flex items-center gap-2 mb-2" data-id="element-196">
                    <div className="w-3 h-3 rounded-full bg-[#04844B]" data-id="element-197" />
                    <span className="text-sm font-medium text-[#706E6B]" data-id="element-198">
                      On Track
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-199">
                    <span className="text-2xl font-semibold text-[#181818]" data-id="element-200">
                      {onTrackCount}
                    </span>
                    <span className="text-sm font-medium text-[#04844B]" data-id="element-201">
                      {onTrackPct}%
                    </span>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-lg border border-[#E5E5E5] shadow-sm" data-id="element-202">
                  <div className="flex items-center gap-2 mb-2" data-id="element-203">
                    <div className="w-3 h-3 rounded-full bg-[#FF9A3C]" data-id="element-204" />
                    <span className="text-sm font-medium text-[#706E6B]" data-id="element-205">
                      At Risk
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-206">
                    <span className="text-2xl font-semibold text-[#181818]" data-id="element-207">
                      {atRiskCount}
                    </span>
                    <span className="text-sm font-medium text-[#B86800]" data-id="element-208">
                      {atRiskPct}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Stacked Bar */}
              {totalAppOut > 0 && <div data-id="element-209">
                  <div className="flex h-3 rounded-full overflow-hidden bg-[#E5E5E5] mb-2" data-id="element-210">
                    <div className="h-full bg-[#04844B] transition-all duration-1000 ease-out" style={{
                width: `${onTrackPct}%`
              }} data-id="element-211" />
                    <div className="h-full bg-[#FF9A3C] transition-all duration-1000 ease-out" style={{
                width: `${atRiskPct}%`
              }} data-id="element-212" />
                  </div>
                  <div className="flex justify-between text-xs text-[#706E6B] font-medium" data-id="element-213">
                    <span data-id="element-214">{onTrackPct}% On Track</span>
                    <span data-id="element-215">{atRiskPct}% At Risk</span>
                  </div>
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </Card>;
}